import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Row, Col, Typography, DatePicker, Card, Tooltip } from 'antd';
import { CloseCircleOutlined, UndoOutlined, CalendarOutlined, FileSearchOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import { getPermission } from '../../constants';

const { Title, Text } = Typography;

const SalesManagement = () => {
    // Retrieve shopId from localStorage
    const shopId = localStorage.getItem('shopId');

    const [salesData, setSalesData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDate, setSelectedDate] = useState(moment());
    const [salesHistory, setSalesHistory] = useState([]);

    const userPermission = getPermission();

    useEffect(() => {
        if (shopId) {
            fetchSalesData(moment());
        } else {
            message.error('Shop ID not found. Please log in again.');
        }
    }, [shopId]);

    const fetchSalesData = async (date) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/sales-summary/${shopId}?date=${date.format('YYYY-MM-DD')}`);
            setSalesData(response.data);
        } catch (error) {
            console.error(error);
            message.error('Failed to retrieve sales data.');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSales = async () => {
        try {
            await axiosInstance.post('/sales/close', { shop_id: shopId });
            message.success('Sales successfully closed for today!');
            fetchSalesData(moment());
        } catch (error) {
            console.error(error);
            message.error('Failed to close sales.');
        }
    };

    const handleReverseSales = async () => {
        try {
            await axiosInstance.post('/sales/reverse', { shop_id: shopId });
            message.success('Sales successfully reversed for today!');
            fetchSalesData(moment());
        } catch (error) {
            console.error(error);
            message.error('Failed to reverse sales.');
        }
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        fetchSalesData(date);
    };

    const renderCurrency = (value) => `Tsh ${parseFloat(value).toFixed(2)}`;

    if (!shopId) {
        return (
            <div style={{ padding: '20px', maxWidth: '100%' }}>
                <Title level={4} style={{ textAlign: 'center', color: 'red' }}>
                    Error: Shop ID not found. Please log in again.
                </Title>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '100%' }}>
            <Row justify="space-between" style={{ marginBottom: '20px' }}>
                <Col>
                    <Title level={3}>Sales Management</Title>
                </Col>
                <Col>
                    <DatePicker
                        value={selectedDate}
                        onChange={handleDateChange}
                        allowClear={false}
                        suffixIcon={<CalendarOutlined />}
                    />
                </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: '20px' }}>
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        title="Close Sales"
                        bordered={false}
                        style={{ textAlign: 'center' }}
                        actions={[
                            <Tooltip title="Close today's sales">
                                <Button type="primary" icon={<CloseCircleOutlined />} onClick={handleCloseSales}>
                                    Close Sales
                                </Button>
                            </Tooltip>,
                        ]}
                    >
                        <Text>Finalize and close today's sales for this shop.</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        title="Reverse Sales"
                        bordered={false}
                        style={{ textAlign: 'center' }}
                        actions={[
                            <Tooltip title="Reverse today's closed sales">
                                <Button type="danger" icon={<UndoOutlined />} onClick={handleReverseSales}>
                                    Reverse Sales
                                </Button>
                            </Tooltip>,
                        ]}
                    >
                        <Text>Undo the sales closure for today.</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        title="Sales History"
                        bordered={false}
                        style={{ textAlign: 'center' }}
                        actions={[
                            <Tooltip title="View detailed sales data">
                                <Button
                                    type="default"
                                    icon={<FileSearchOutlined />}
                                    onClick={() => setModalVisible(true)}
                                >
                                    View Details
                                </Button>
                            </Tooltip>,
                        ]}
                    >
                        <Text>Retrieve and analyze closed sales data by date.</Text>
                    </Card>
                </Col>
            </Row>

            {salesData && (
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Card title="Total Sales" bordered={false}>
                            <Title level={4}>{renderCurrency(salesData.total_sales)}</Title>
                        </Card>
                    </Col>
                    {userPermission?.is_view_profit &&
                        <Col xs={24} md={12}>
                            <Card title="Total Profit" bordered={false}>
                                <Title level={4}>{renderCurrency(salesData.total_profit)}</Title>
                            </Card>
                        </Col>
                    }
                </Row>
            )}

            <Modal
                title="Sales History Details"
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={800}
            >
                {salesData ? (
                    <>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Title level={5}>Product Breakdown</Title>
                                <Table
                                    dataSource={salesData.product_breakdown}
                                    rowKey="sale_id"
                                    pagination={false}
                                    columns={[
                                        {
                                            title: 'Product Name',
                                            dataIndex: ['items', 0, 'product_name'],
                                            key: 'product_name',
                                        },
                                        {
                                            title: 'Quantity',
                                            dataIndex: ['items', 0, 'quantity'],
                                            key: 'quantity',
                                        },
                                        {
                                            title: 'Selling Price',
                                            dataIndex: ['items', 0, 'selling_price'],
                                            key: 'selling_price',
                                            render: (value) => renderCurrency(value),
                                        },
                                        {
                                            title: 'Profit',
                                            dataIndex: ['items', 0, 'profit'],
                                            key: 'profit',
                                            render: (value) => renderCurrency(value),
                                        },
                                    ]}
                                />
                            </Col>
                        </Row>
                    </>
                ) : (
                    <p>Loading sales data...</p>
                )}
            </Modal>
        </div>
    );
};

export default SalesManagement;
